import { useCallback, useMemo } from 'react';
import { getCountryDataList, ICountryData } from 'countries-list';
import { get, useFormContext } from 'react-hook-form';

import { Combobox } from '@components/patterns';
import { FormField, Input } from '@components/primitives';

import {
  getCountryFromCode,
  getStateProvinceFromCode,
  getStateProvincesList,
  hasStateProvinces,
  IAddress,
  IStateProvince,
} from '@utils/address';

import css from './styles.module.css';

const countries = getCountryDataList();

export interface AddressInputProps {
  name?: string;
  disabled?: boolean;
}

export function AddressInput({
  name = 'address',
  disabled,
}: AddressInputProps) {
  const methods = useFormContext<{ [key: typeof name]: IAddress }>();
  const {
    setValue,
    formState: { errors },
  } = methods;

  const country = methods.watch(`${name}.country`);
  const currentCountry = useMemo(() => getCountryFromCode(country), [country]);
  const onSelectCountry = useCallback(
    (country: ICountryData) => {
      setValue(`${name}.state`, '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`${name}.country`, country.iso2, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const stateOptions = useMemo(() => {
    if (!country) return [];
    if (!hasStateProvinces(country)) return [];
    return getStateProvincesList(country);
  }, [country]);

  const state = methods.watch(`${name}.state`);
  const currentState = useMemo(
    () => (country && state ? getStateProvinceFromCode(country, state) : null),
    [stateOptions, state]
  );
  const onSelectState = useCallback(
    (state: IStateProvince) => {
      setValue(`${name}.state`, state.iso2, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  // Only Canada uses "province" label
  const stateLabel = country === 'CA' ? 'Province' : 'State';
  // Only US and Philippines use "zip codes"
  const postalCodeLabel =
    country === 'US' || country === 'PH' ? 'Zip code' : 'Postal code';

  return (
    <div className={css.address}>
      <FormField
        label="Address"
        hideLabel
        error={get(errors, `${name}.line1.message`)}
      >
        <Input
          {...methods.register(`${name}.line1`, { disabled })}
          placeholder="123 Easy St"
        />
      </FormField>

      <FormField
        label="Apartment, suite, etc."
        hideLabel
        error={get(errors, `${name}.line2.message`)}
      >
        <Input
          {...methods.register(`${name}.line2`, { disabled })}
          placeholder="Apartment, suite, etc."
        />
      </FormField>

      <FormField
        label="City"
        hideLabel
        error={get(errors, `${name}.city.message`)}
      >
        <Input
          {...methods.register(`${name}.city`, { disabled })}
          placeholder="City"
        />
      </FormField>

      <FormField
        label="Country"
        hideLabel
        error={get(errors, `${name}.country.message`)}
      >
        <Combobox
          {...methods.register(`${name}.country`, { disabled })}
          placeholder="Country"
          value={currentCountry ?? undefined}
          onValueChange={onSelectCountry}
          items={countries}
          getOption={(item) => ({
            value: item.iso2,
            keywords: [item.name],
            label: item.name,
          })}
        />
      </FormField>

      {stateOptions.length > 0 && (
        <FormField
          label={stateLabel}
          hideLabel
          error={get(errors, `${name}.state.message`)}
        >
          <Combobox
            {...methods.register(`${name}.state`, { disabled })}
            placeholder={stateLabel}
            value={currentState ?? undefined}
            onValueChange={onSelectState}
            items={stateOptions}
            getOption={(item) => ({
              value: item.iso2,
              keywords: [item.name],
              label: item.name,
            })}
          />
        </FormField>
      )}

      <FormField
        label={postalCodeLabel}
        hideLabel
        error={get(errors, `${name}.postalCode.message`)}
      >
        <Input
          {...methods.register(`${name}.postalCode`)}
          placeholder={postalCodeLabel}
        />
      </FormField>
    </div>
  );
}
